'use client';

import { postNotifyProduct } from '@/api/product';
import Button from '@/components/Button';
import Popup from '@/components/Popup';
import { colors, font } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { checkEmail } from '@/utils/tools';
import { TextField, Typography } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import React, { useState } from 'react';

interface NotifyEmailDialogProps {
  open: boolean;
  productId: number;
  onClose: () => void;
  isWeb: boolean;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
}

const NotifyEmailDialog: React.FC<NotifyEmailDialogProps> = ({
  open,
  productId,
  onClose,
  isWeb,
  SDProductDetailsPageBtnClick,
}) => {
  const t = useTranslations('goodsDetail.notifyEmailDialog');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);

  // 邮箱验证
  const validateEmail = (value: string): boolean => {
    if (!value) {
      setEmailError(t('emailRequired'));
      return false;
    }

    if (!checkEmail(value)) {
      setEmailError(t('pleaseEnterValidEmail'));
      return false;
    }

    setEmailError('');
    return true;
  };

  // 处理邮箱输入变化
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEmail(value);
    if (emailError && value) {
      validateEmail(value);
    }
  };

  // 处理提交
  const handleSubmit = async () => {
    SDProductDetailsPageBtnClick?.('Notify me Submit');
    if (!validateEmail(email)) {
      return;
    }

    setLoading(true);
    try {
      await postNotifyProduct({
        email,
        id: productId,
      });

      // 成功后关闭对话框并重置表单
      handleClose();
    } catch (error) {
      logger.error('Notify email submission failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理关闭
  const handleClose = () => {
    setEmail('');
    setEmailError('');
    setLoading(false);
    onClose();
  };

  // 处理键盘事件
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const renderContent = () => {
    return (
      <div className={`${isWeb ? 'flex-1' : 'pb-[81px]'}`}>
        {!isWeb && (
          <Typography
            component="label"
            sx={{
              display: 'block',
              marginBottom: '16px',
              fontSize: '14px',
              fontFamily: font.bb,
            }}
          >
            {t('emailAddress')}
          </Typography>
        )}

        <TextField
          fullWidth
          value={email}
          onChange={handleEmailChange}
          onKeyPress={handleKeyPress}
          placeholder={t('pleaseEnterEmailAddress')}
          error={!!emailError}
          helperText={emailError}
          inputProps={{
            maxLength: 50,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: isWeb ? '40px' : '45px',
              backgroundColor: '#fff',
              borderRadius: isWeb ? '6px 0px 0px 6px' : '6px',
              '&:hover fieldset': {
                borderColor: colors.auxiliaryLightGrayBlue,
              },
              '&.Mui-error fieldset': {
                borderColor: colors.error,
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '10px 12px',
              '&::placeholder': {
                color: '#ccc',
                opacity: 1,
              },
            },
            '& .MuiFormHelperText-root': {
              marginTop: '4px',
              color: colors.error,
            },
            '&.MuiTextField-root': {
              marginTop: isWeb ? '0px' : '8px',
            },
          }}
        />
      </div>
    );
  };
  if (isWeb && open) {
    return (
      <div className="flex items-start mt-18">
        {renderContent()}
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            width: '97px',
            height: '40px',
            borderRadius: '0px 6px 6px 0px',
            fontSize: '14px',
            fontFamily: font.bb,
            backgroundColor: colors.primaryPurple,
            '&:hover': {
              backgroundColor: colors.primaryPurpleHover,
            },
          }}
          loading={loading}
        >
          {t('submit')}
        </Button>
      </div>
    );
  }
  if (isWeb && !open) {
    return null;
  }
  return (
    <Popup
      open={open}
      onClose={handleClose}
      title={t('notifyMeWhenInStock')}
      position="bottom"
      height="323px"
      round
    >
      {/* 内容区 */}
      {renderContent()}
      {/* 底部按钮区 */}
      <div
        className="absolute bottom-0 left-0 w-full h-[66px] flex items-center justify-center bg-white"
        style={{
          boxShadow: '0px -1px 5px 0px rgba(204,204,204,0.5)',
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            width: '285px',
            height: '40px',
            borderRadius: '22px',
            fontSize: '14px',
            fontFamily: font.bb,
            backgroundColor: colors.primaryPurple,
            '&:hover': {
              backgroundColor: colors.primaryPurpleHover,
            },
          }}
          loading={loading}
        >
          {t('submit')}
        </Button>
      </div>
    </Popup>
  );
};

export default NotifyEmailDialog;
